import { createContext, useCallback, useReducer, useEffect } from "react";
import useAuth from "../../hooks/useAuth";
import useHome from "../../hooks/user/useHome";

import axios from "../../utils/axios/v1/userAxios";
import { ConfigAction, WafType } from "../../utils/constants";

const GET_SITES = "GET_SITES";
const GET_SITES_FOR_ITEMS = "GET_SITES_FOR_ITEMS";
const GET_SITES_FOR_LIST = "GET_SITES_FOR_LIST";
const CLEAR_SITES = "CLEAR_SITES";
const PATCH_SITE_WIZARD_STEP = "PATCH_SITE_WIZARD_STEP";
const GET_WAF_BASIS = "GET_WAF_BASIS";
const CONFIG_SITE = "CONFIG_SITE";
const GET_REGIONAL_DETECTIONS = "GET_REGIONAL_DETECTIONS";
const GET_TOP_SOURCE = "GET_TOP_SOURCE";
const GET_TOP_REGION = "GET_TOP_REGION";
const GET_TOP_PATH = "GET_TOP_PATH";
const GET_TOP_UA = "GET_TOP_UA";
const GET_TOP_DETECTION_TYPE = "GET_TOP_DETECTION_TYPE";
const GET_TOP_METHOD = "GET_TOP_METHOD";
const GET_TOP_RESPONSE_CODE = "GET_TOP_RESPONSE_CODE";
const GET_SIGS = "GET_SIGS";
const GET_MLS = "GET_MLS";
const GET_SD_SIGS = "GET_SD_SIGS";
const GET_SMART_PARSES = "GET_SMART_PARSES";
const GET_EVENTS = "GET_EVENTS";
const GET_TOTAL_EVENTS_COUNT = "GET_TOTAL_EVENTS_COUNT";
const SET_CURRENT_STATUS = "SET_CURRENT_STATUS";
const SET_ERROR = "SET_ERROR";

const initialState = {
  sites: null,
  time_range: { period: "7d" },
  siteList: null,
  site_List: null,
  cursite: null,
  regional_detections: null,
  top_source_detection: null,
  top_path: null,
  top_ua: null,
  top_detection_type: null,
  top_method: null,
  top_response_code: null,
  top_region_detection: null,
  basis_waf: {
    total_request: { now: null, past: null },
    sig_blocked: { now: null, past: null },
    sig_challenged: { now: null, past: null },
    ai_blocked: { now: null, past: null },
    ai_challenged: { now: null, past: null },
    sd_sig_blocked: { now: null, past: null },
    sd_sig_challenged: { now: null, past: null },
  },
  sigs: null,
  mls: null,
  sdsigs: null,
  smartparses: null,

  // filter
  events: null,
  total_events_count: 0,
  from: 0,
  rows_per_page: 5,
  filter: null,
  sites_total: 0,
  ///
  errMsg: null,
};

const SiteReducer = (state, action) => {
  switch (action.type) {
    case GET_SITES:
      return {
        ...state,
        sites: action.payload.sites,
        sites_total:
          typeof action.payload.sites_total === "number"
            ? action.payload.sites_total
            : Array.isArray(action.payload.sites)
            ? action.payload.sites.length
            : state.sites_total,
      };
    case GET_SITES_FOR_ITEMS:
      return {
        ...state,
        siteList: action.payload.siteList,
      };
    case GET_SITES_FOR_LIST:
      return {
        ...state,
        site_List: action.payload.site_List,
      };
    case CLEAR_SITES:
      return {
        ...state,
        sites: null,
        site_List: null,
        sites_total: 0,
      };
    case PATCH_SITE_WIZARD_STEP: {
      const { site_uid, wizard_step } = action.payload;
      const patchRow = (row) =>
        row && String(row.id) === String(site_uid) ? { ...row, wizard_step } : row;
      return {
        ...state,
        sites: Array.isArray(state.sites) ? state.sites.map(patchRow) : state.sites,
        site_List: Array.isArray(state.site_List) ? state.site_List.map(patchRow) : state.site_List,
        cursite:
          state.cursite && String(state.cursite.id) === String(site_uid)
            ? { ...state.cursite, wizard_step }
            : state.cursite,
      };
    }
    case GET_WAF_BASIS:
      return {
        ...state,
        basis_waf: action.payload.basis_waf,
      };
    case CONFIG_SITE:
      return {
        ...state,
        cursite: action.payload.cursite,
      };
    case GET_REGIONAL_DETECTIONS:
      return {
        ...state,
        regional_detections: action.payload.regional_detections,
      };
    case GET_TOP_SOURCE:
      return {
        ...state,
        top_source_detection: action.payload.top_source_detection,
      };
    case GET_TOP_REGION:
      return {
        ...state,
        top_region_detection: action.payload.top_region_detection,
      };
    case GET_TOP_PATH:
      return {
        ...state,
        top_path: action.payload.top_path,
      };
    case GET_TOP_UA:
      return {
        ...state,
        top_ua: action.payload.top_ua,
      };
    case GET_TOP_DETECTION_TYPE:
      return {
        ...state,
        top_detection_type: action.payload.top_detection_type,
      };
    case GET_TOP_METHOD:
      return {
        ...state,
        top_method: action.payload.top_method,
      };
    case GET_TOP_RESPONSE_CODE:
      return {
        ...state,
        top_response_code: action.payload.top_response_code,
      };
    case GET_SIGS:
      return {
        ...state,
        sigs: action.payload.sigs,
      };
    case GET_MLS:
      return {
        ...state,
        mls: action.payload.mls,
      };
    case GET_SD_SIGS:
      return {
        ...state,
        sdsigs: action.payload.sdsigs,
      };
    case GET_SMART_PARSES:
      return {
        ...state,
        smartparses: action.payload.smartparses,
      };
    case GET_EVENTS:
      return {
        ...state,
        events: action.payload.events,
      };
    case GET_TOTAL_EVENTS_COUNT:
      return {
        ...state,
        total_events_count: action.payload.total_events_count,
      };
    case SET_CURRENT_STATUS:
      return {
        ...state,
        time_range: action.payload.time_range,
        rows_per_page: action.payload.rows_per_page,
        from: action.payload.from,
        filter: action.payload.filter,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const SiteContext = createContext(null);

let eventController;

function SiteProvider({ children }) {
  const { setAccessToken } = useAuth();
  const { curProjectId, curSiteId } = useHome();
  const [state, dispatch] = useReducer(SiteReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);
  const getSites = useCallback(
    async (setWebsiteController, listOpts = null) => {
      setErr(null);
      const controller = new AbortController();
      const signal = controller.signal;
      if (typeof setWebsiteController === "function") {
        setWebsiteController(controller);
      }
      try {
        const params = {};
        if (listOpts && typeof listOpts === "object") {
          if (listOpts.page !== undefined && listOpts.page !== null) params.page = listOpts.page;
          if (listOpts.limit !== undefined && listOpts.limit !== null) params.limit = listOpts.limit;
          if (listOpts.project_id) params.project_id = listOpts.project_id;
          if (listOpts.status) params.status = listOpts.status;
          if (listOpts.enforcement) params.enforcement = listOpts.enforcement;
          if (listOpts.q !== undefined && listOpts.q !== null && String(listOpts.q).trim() !== "") {
            params.q = String(listOpts.q).trim();
          }
        }
        const response = await axios.get("/site", { signal, params });
        const data = response.data;
        const isPaged = data && !Array.isArray(data) && Array.isArray(data.items);
        dispatch({
          type: GET_SITES,
          payload: {
            sites: isPaged ? data.items : data,
            sites_total: isPaged ? data.total : Array.isArray(data) ? data.length : 0,
          },
        });
        return isPaged ? data : { items: data, total: Array.isArray(data) ? data.length : 0 };
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_SITES,
          payload: {
            sites: [],
            sites_total: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const getSitesForItems = useCallback(async (forceRefresh = false) => {
    try {
      await setAccessToken();
      
      // Build query parameters based on selected project
      let url = "/site/basis";
      if (curProjectId && curProjectId !== "all") {
        const params = new URLSearchParams();
        if (curProjectId === "default") {
          // Pass a special parameter for unassigned sites
          params.append("project_id", "default");
        } else {
          params.append("project_id", curProjectId);
        }
        url = `${url}?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      dispatch({
        type: GET_SITES_FOR_ITEMS,
        payload: {
          siteList: response.data,
        },
      });
    } catch (err) {
      setErr(err.message);
    }
  }, [setErr, setAccessToken, curProjectId]);
  const getSitesForList = useCallback(async () => {
    try {
      const response = await axios.get("/site/basis_all");
      dispatch({
        type: GET_SITES_FOR_LIST,
        payload: {
          site_List: response.data,
        },
      });
    } catch (err) {
      dispatch({
        type: GET_SITES_FOR_LIST,
        payload: {
          site_List: [],
        },
      });
      setErr(err.message);
    }
  }, [setErr]);

  const clearSites = useCallback(() => {
    dispatch({
      type: CLEAR_SITES,
    });
  }, []);

  const createSite = useCallback(
    async (siteID, siteIP, projectId) => {
      try {
        const payload = {
          site_id: siteID,
          site_addr: siteIP,
          site_name: siteID,
          subdomains: [],
        };
        if (projectId && projectId !== "") {
          payload.project_id = projectId;
        }
        const response = await axios.post("/site", payload);
        clearSites();
        getSitesForItems();
        return { status: "success", msg: response.data };
      } catch (err) {
        return { status: "fail", msg: err.message };
      }
    },
    [getSitesForItems, clearSites]
  );
  // MCP_ONBOARD_WARN_2026_11_03 — MCP endpoints on other sites that would
  // stop being enforced if this hostname were onboarded. Never blocks: a
  // failed check returns null and onboarding carries on.
  const getMcpOnboardImpact = useCallback(async (siteID) => {
    try {
      const response = await axios.post("/site/mcp_impact", { site_id: siteID });
      return response.data;
    } catch (err) {
      return null;
    }
  }, []);
  const settingApply = useCallback(async (siteID, action = ConfigAction.ALL) => {
    try {
      const response = await axios.post("/site/applyConfig", {
        site_id: siteID,
        action: action,
      });
      return { msg: response.data.msg, status: "success" };
    } catch (err) {
      let error_msg = err.message;
      return { msg: error_msg, status: "error" };
    }
  }, []);
  const onCreateSuccess = useCallback(async (siteID) => {
    try {
      const response = await axios.post("/site/onCreate", {
        site_id: siteID,
      });
      return { msg: response.data.msg, status: "success" };
    } catch (err) {
      let error_msg = err.message;
      return { msg: error_msg, status: "error" };
    }
  }, []);

  const updateSite = useCallback(
    async (site_uid, values, subDomainList) => {
      if (subDomainList) {
        const tmpDomains = [];
        for (let index = 0; index < subDomainList.length; index++) {
          const sub = subDomainList[index];
          const name = String(sub?.name || "").trim();
          if (!name) continue;
          tmpDomains.push({
            name,
            addr: String(sub?.addr || "").trim(),
            enabled: sub?.enabled !== undefined ? !!sub.enabled : true,
          });
        }
        values.subdomains = tmpDomains;
      }
      await axios.put(`/site/${site_uid}`, values);
      clearSites();
      getSitesForItems();
    },
    [getSitesForItems, clearSites]
  );

  /** Persist Add Domain 2 page and patch local Sites caches so Resume first-paint is not stale. */
  const saveSiteWizardStep = useCallback(async (site_uid, wizard_step) => {
    if (!site_uid) return;
    // Optimistic cache update so Resume / list merge sees the new step immediately.
    dispatch({
      type: PATCH_SITE_WIZARD_STEP,
      payload: { site_uid, wizard_step },
    });
    await axios.put(`/site/${site_uid}`, { wizard_step });
  }, []);

  const deleteSite = useCallback(
    async (siteID, removeFlag) => {
      try {
        if (removeFlag) {
          await axios.delete("/site", {
            data: { site_id: siteID },
          });
        } else {
          await axios.patch("/site/bulkDelete", {
            site_id: siteID,
            deleted: true,
          });
        }
        clearSites();
        getSitesForItems();
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getSitesForItems, clearSites]
  );
  const restoreSite = useCallback(
    async (siteID) => {
      try {
        await axios.patch("/site/bulkDelete", {
          site_id: siteID,
          deleted: false,
        });
        clearSites();
        getSitesForItems();
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getSitesForItems, clearSites]
  );

  const viewSite = useCallback(async (siteID) => {
    const response = await axios.get(`/site/${siteID}`);
    return response.data;
  }, []);
  const selectSite = useCallback(
    async (siteID) => {
      if (siteID === "all") {
        dispatch({
          type: CONFIG_SITE,
          payload: {
            cursite: { site_id: "all" },
          },
        });
        dispatch({
          type: SET_ERROR,
          payload: {
            errMsg: null,
          },
        });
      } else {
        try {
          const response = await axios.get(`/site`, {
            params: { site_id: siteID },
          });
          dispatch({
            type: CONFIG_SITE,
            payload: {
              cursite: response.data,
            },
          });
          dispatch({
            type: SET_ERROR,
            payload: {
              errMsg: null,
            },
          });
          return response.data;
        } catch (err) {
          dispatch({
            type: CONFIG_SITE,
            payload: {
              cursite: null,
            },
          });
          setErr(err.message);
        }
      }
    },
    [setErr]
  );

  useEffect(() => {
    if (curSiteId) {
      selectSite(curSiteId);
    }
  }, [curSiteId, selectSite]);

  // const changeSite = (siteID, ruleID) => {
  //     axios.get(`/site/${siteID}`).then((response) => {
  //         const cursite = response.data;
  //         dispatch({
  //             type: CONFIG_SITE,
  //             payload: {
  //                 cursite: cursite,
  //             },
  //         });
  //         window.localStorage.setItem("cursite", siteID);
  //     });

  //     axios
  //         .post("/config/waf/get_crs_rule", {
  //             site_id: siteID,
  //             rule_id: ruleID,
  //         })
  //         .then((response) => {
  //             const currule = response.data;
  //             dispatch({
  //                 type: SELECT_RULE,
  //                 payload: {
  //                     currule: currule,
  //                 },
  //             });
  //         });
  // };
  const getRegionalDetections = useCallback(
    async (site_id, time_range, signal) => {
      dispatch({
        type: GET_REGIONAL_DETECTIONS,
        payload: {
          regional_detections: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_regional_detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_REGIONAL_DETECTIONS,
          payload: {
            regional_detections: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_REGIONAL_DETECTIONS,
          payload: {
            regional_detections: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopSource = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_SOURCE,
        payload: {
          top_source_detection: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_top_source_detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source_detection: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source_detection: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopRegion = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_REGION,
        payload: {
          top_region_detection: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_top_region_detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_REGION,
          payload: {
            top_region_detection: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_REGION,
          payload: {
            top_region_detection: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopPath = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_PATH,
        payload: {
          top_path: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_top_path",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopUA = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_UA,
        payload: {
          top_ua: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_top_ua",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopDetectionType = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_DETECTION_TYPE,
        payload: {
          top_detection_type: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_top_detection_type",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_DETECTION_TYPE,
          payload: {
            top_detection_type: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_DETECTION_TYPE,
          payload: {
            top_detection_type: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopMethod = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_METHOD,
        payload: {
          top_method: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_top_method",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopResponseCode = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_RESPONSE_CODE,
        payload: {
          top_response_code: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/top_res_code_detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getWAFBasis = useCallback(
    async (site_id, time_range, signal) => {
      dispatch({
        type: GET_WAF_BASIS,
        payload: {
          basis_waf: {
            total_request: { now: null, past: null },
            sig_blocked: { now: null, past: null },
            sig_challenged: { now: null, past: null },
            ai_blocked: { now: null, past: null },
            ai_challenged: { now: null, past: null },
            sd_sig_blocked: { now: null, past: null },
            sd_sig_challenged: { now: null, past: null },
          },
        },
      });
      try {
        const response = await axios.post(
          "stats/basis_waf",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_WAF_BASIS,
          payload: {
            basis_waf: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_WAF_BASIS,
          payload: {
            basis_waf: {
              total_request: { now: 0, past: 0 },
              sig_blocked: { now: 0, past: 0 },
              sig_challenged: { now: 0, past: 0 },
              ai_blocked: { now: 0, past: 0 },
              ai_challenged: { now: 0, past: 0 },
              sd_sig_blocked: { now: 0, past: 0 },
              sd_sig_challenged: { now: 0, past: 0 },
            },
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getSigs = useCallback(
    async (site_id, time_range, signal) => {
      dispatch({
        type: GET_SIGS,
        payload: {
          sigs: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            waf_type: WafType.SIGNATURE,
          },
          { signal }
        );
        dispatch({
          type: GET_SIGS,
          payload: {
            sigs: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_SIGS,
          payload: {
            sigs: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getSdSigs = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            waf_type: WafType.SENSEDEFENCE_SIGNATURE,
          },
          { signal }
        );
        dispatch({
          type: GET_SD_SIGS,
          payload: {
            sdsigs: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_SD_SIGS,
          payload: {
            sdsigs: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getSmartParses = useCallback(
    async (site_id, time_range, signal) => {
      dispatch({
        type: GET_SMART_PARSES,
        payload: {
          smartparses: null,
        },
      });
      try {
        const response = await axios.post(
          "stats/smart_parse_detection",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_SMART_PARSES,
          payload: {
            smartparses: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_SMART_PARSES,
          payload: {
            smartparses: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getDashboardInfo = useCallback(
    (setWafDashController, site_id, time_range) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setWafDashController(controller);
      dispatch({
        type: SET_CURRENT_STATUS,
        payload: {
          time_range: time_range,
          rows_per_page: 5,
          from: 0,
          filter: [],
        },
      });

      getRegionalDetections(site_id, time_range, signal);
      getTopRegion(site_id, time_range, 5, signal);
      getTopSource(site_id, time_range, 5, signal);
      getTopPath(site_id, time_range, 5, signal);
      getTopUA(site_id, time_range, 5, signal);
      getTopDetectionType(site_id, time_range, 5, signal);
      getTopMethod(site_id, time_range, 5, signal);
      getWAFBasis(site_id, time_range, signal);
      getSigs(site_id, time_range, signal);
      dispatch({
        type: GET_MLS,
        payload: { mls: [] },
      });
      dispatch({
        type: GET_SD_SIGS,
        payload: { sdsigs: [] },
      });
      getSmartParses(site_id, time_range, signal);
    },
    [
      getRegionalDetections,
      getTopRegion,
      getTopSource,
      getTopPath,
      getTopUA,
      getTopDetectionType,
      getTopMethod,
      getWAFBasis,
      getSigs,
      getSmartParses,
    ]
  );
  const getTopEventsInfo = useCallback(
    (setWafEventController, site_id, time_range) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setWafEventController(controller);

      getTopRegion(site_id, time_range, 20, signal);
      getTopSource(site_id, time_range, 20, signal);
      getTopPath(site_id, time_range, 20, signal);
      getTopUA(site_id, time_range, 20, signal);
      getTopDetectionType(site_id, time_range, 20, signal);
      getTopMethod(site_id, time_range, 20, signal);
      getTopResponseCode(site_id, time_range, 20, signal);
    },
    [getTopRegion, getTopSource, getTopPath, getTopUA, getTopDetectionType, getTopMethod, getTopResponseCode]
  );
  const getOnlyEvents = useCallback(
    async (site_id, time_range, rows_per_page, from, filter) => {
      dispatch({
        type: GET_EVENTS,
        payload: {
          events: null,
        },
      });
      dispatch({
        type: SET_CURRENT_STATUS,
        payload: {
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
          filter: filter,
        },
      });
      const value = {
        site_id: site_id,
        project_id: curProjectId,
        time_range: time_range,
        count: rows_per_page,
        from: from,
        conditions: filter,
      };

      if (eventController) eventController.abort();
      eventController = new AbortController();
      const signal = eventController.signal;
      try {
        const response = await axios.post("log/waf_event", value, {
          signal,
        });
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const setTotalCountToZero = useCallback(() => {
    dispatch({
      type: GET_TOTAL_EVENTS_COUNT,
      payload: {
        total_events_count: 0,
      },
    });
  }, []);

  const getAllProjectList = useCallback(async () => {
    try {
      const response = await axios.get("/project");
      return response.data;
    } catch (err) {
      setErr(err.message);
      return { projects: [], sites: [] };
    }
  }, [setErr]);

  const getOneProjectInfo = useCallback(
    async (projectId) => {
      try {
        const response = await axios.get(`/project/${projectId}`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return {};
      }
    },
    [setErr]
  );

  const addProject = useCallback(async (value) => {
    try {
      const response = await axios.post(`/project`, value);
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || err.message || "Failed to create project";
      throw new Error(message);
    }
  }, []);

  const updateProject = useCallback(async (projectId, value) => {
    try {
      const response = await axios.put(`/project/${projectId}`, value);
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || err.message || "Failed to update project";
      throw new Error(message);
    }
  }, []);

  const deleteProject = useCallback(
    async (projectId) => {
      try {
        const response = await axios.delete(`/project`, {
          data: { project_id: projectId },
        });
        return response.data;
      } catch (err) {
        setErr(err.message);
        return {};
      }
    },
    [setErr]
  );

  const moveProject = useCallback(
    async (projectId1, projectId2) => {
      try {
        const response = await axios.patch(`/project/move_project/${projectId1}/${projectId2}`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return {};
      }
    },
    [setErr]
  );

  const moveSite = useCallback(
    async (projectId, sites) => {
      try {
        const response = await axios.patch(`/project/move_site/${projectId}`, sites);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return {};
      }
    },
    [setErr]
  );

  const deleteSiteFromProject = useCallback(
    async (siteId) => {
      try {
        const response = await axios.delete(`/project/remove_site/${siteId}`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return {};
      }
    },
    [setErr]
  );

  return (
    <SiteContext.Provider
      value={{
        ...state,
        getSites,
        getSitesForItems,
        getSitesForList,
        clearSites,
        createSite,
        getMcpOnboardImpact,
        settingApply,
        onCreateSuccess,
        updateSite,
        saveSiteWizardStep,
        deleteSite,
        restoreSite,
        viewSite,
        selectSite,
        getRegionalDetections,
        getTopSource,
        getTopRegion,
        getTopPath,
        getTopUA,
        getTopDetectionType,
        getTopMethod,
        getTopResponseCode,
        getWAFBasis,
        getSigs,
        getDashboardInfo,
        getOnlyEvents,
        setTotalCountToZero,
        getTopEventsInfo,
        setErr,
        getAllProjectList,
        getOneProjectInfo,
        addProject,
        updateProject,
        deleteProject,
        moveProject,
        moveSite,
        deleteSiteFromProject,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
}

export { SiteContext, SiteProvider };
